import React, { useState } from 'react';
import AuthShell from '../components/AuthShell';
import {
  getEmailRedirectUrl,
  supabase
} from '../lib/supabaseClient';

function Auth() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const isSignup = mode === 'signup';
  const trimmedEmail = email.trim();

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setError('');
    setNotice('');
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!trimmedEmail || !password) {
      setError('Enter an email and password to continue.');
      return;
    }

    setBusy(true);

    try {
      if (isSignup) {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email: trimmedEmail,
          password,
          options: {
            emailRedirectTo: getEmailRedirectUrl()
          }
        });

        if (signUpError) throw signUpError;

        if (!data.user?.identities?.length) {
          setError('An account with this email already exists. Try logging in.');
          return;
        }

        if (!data.session) {
          setNotice(
            'Check your email to confirm your account before signing in.'
          );
          return;
        }

        return;
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password
      });

      if (signInError) throw signInError;
    } catch (caught) {
      setError(caught.message || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleMagicLink = async () => {
    setError('');
    setNotice('');

    if (!trimmedEmail) {
      setError('Enter your email to receive a login link.');
      return;
    }

    setBusy(true);

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: trimmedEmail,
        options: {
          emailRedirectTo: getEmailRedirectUrl()
        }
      });

      if (otpError) throw otpError;

      setNotice('Check your email for a login link.');
    } catch (caught) {
      setError(caught.message || 'Could not send a login link.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      kicker={isSignup ? 'Create your account' : 'Welcome back'}
      subtitle={isSignup
        ? 'Use an email and password, then confirm the message we send you.'
        : 'Sign in with your password, or email yourself a magic link.'}
      title={isSignup ? 'Join Quantix' : 'Sign in to Quantix'}
    >
      <div className="mb-6 flex rounded-lg bg-[#f0f0ec] p-1">
        {[
          { id: 'login', label: 'Log in' },
          { id: 'signup', label: 'Create account' }
        ].map((tab) => (
          <button
            className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition ${
              mode === tab.id
                ? 'bg-white text-[#24302d] shadow-sm'
                : 'text-[#7b8581] hover:text-[#24302d]'
            }`}
            key={tab.id}
            onClick={() => handleModeChange(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form className="flex flex-col gap-4" onSubmit={handlePasswordSubmit}>
        <label className="flex flex-col gap-1.5 text-sm font-semibold text-[#24302d]">
          Email
          <input
            autoComplete="email"
            className="rounded-xl border border-[#e3e4de] bg-white px-4 py-3 text-sm font-normal outline-none transition focus:border-[#a78bfa] focus:shadow-[0_0_0_3px_#f3e8ff]"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            type="email"
            value={email}
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm font-semibold text-[#24302d]">
          Password
          <input
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            className="rounded-xl border border-[#e3e4de] bg-white px-4 py-3 text-sm font-normal outline-none transition focus:border-[#a78bfa] focus:shadow-[0_0_0_3px_#f3e8ff]"
            minLength={6}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={isSignup ? 'At least 6 characters' : 'Your password'}
            type="password"
            value={password}
          />
        </label>

        {error ? (
          <p className="rounded-xl bg-[#fff0ea] px-3 py-2 text-sm text-[#ba503c]">
            {error}
          </p>
        ) : null}

        {notice ? (
          <p className="rounded-xl bg-[#e6f0ec] px-3 py-2 text-sm text-[#4f776d]">
            {notice}
          </p>
        ) : null}

        <button
          className="rounded-xl bg-[#7c3aed] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#5b21b6] disabled:cursor-not-allowed disabled:opacity-50"
          disabled={busy}
          type="submit"
        >
          {busy
            ? 'Please wait…'
            : isSignup
              ? 'Create account'
              : 'Log in'}
        </button>
      </form>

      <div className="mt-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wide text-[#9aa19d]">
        <span className="h-px flex-1 bg-[#e3e4de]" />
        or
        <span className="h-px flex-1 bg-[#e3e4de]" />
      </div>

      <button
        className="mt-4 w-full rounded-xl border border-[#e3e4de] bg-white px-5 py-3 text-sm font-semibold text-[#24302d] transition hover:border-[#7c3aed] disabled:cursor-not-allowed disabled:opacity-50"
        disabled={busy}
        onClick={handleMagicLink}
        type="button"
      >
        Email me a login link
      </button>
    </AuthShell>
  );
}

export default Auth;
